import type { ReactNode } from 'react';
import type { ApproveReferralClaimDto, PaymentProviderName, Player, TransactionStatus } from '@alien-platform/bo-core';
import type { AddJbWithdrawRequestResponse } from '@alien-platform/bo-core/lib/transaction/types/jb/add-jb-withdraw-request.interface';
import type {
  GetPlayerCashbackReportDto,
  OperatorInvoice,
  OperatorPayment,
  OverridePlayerAffiliateSettingsDto,
  ResetPlayerSettingsOverridesDto,
  TestCalculateProfitDto,
  WithdrawAffiliateProfitDto,
} from '@alien-platform/bo-sdk';
import type {
  IAffiliateLevelSettings,
  IAffiliateLevelSettingsPPF,
  IAffiliateRevenueShareSettings,
  IAffiliateRevenueShareSettingsPPF,
  IPlayerCashbackReport,
  IPlayerCashbackSettingsReport,
} from '@ap-db-worker/common';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  GeneralModal,
  type AffiliateReportsHelpInfo,
  type AffiliateSettingsHelp,
  type CashbackDescriptions,
} from '@/types/general';

import type { RootState } from '..';

interface ModalContentSliceState {
  [GeneralModal.PLAYER_DELETE_CONFIRMATION]: { playerId: string } | null;
  [GeneralModal.VERIFY_DEPOSIT_CONFIRMATION]: {
    txId: string;
    isNewVersion?: boolean;
  } | null;
  [GeneralModal.CHECK_ORDER_STATUS]: unknown;
  [GeneralModal.LAUNCH_GAME_URL]: { link: string } | null;
  [GeneralModal.REJECT_DEPOSIT_ORDER]: { txId: string } | null;
  [GeneralModal.APPROVE_REJECT_WITHDRAW_CONFIRMATION]: {
    txId: string;
    playerNote: string;
    actionType: string;
    isNewVersion?: boolean;
    paymentProvider: string;
    status: TransactionStatus;
  } | null;
  [GeneralModal.APPROVE_REJECT_BONUS_CONFIRMATION]: {
    txId: string;
    playerNote: string;
    actionType: string;
  } | null;
  [GeneralModal.APPROVE_REJECT_CASHBACK_CONFIRMATION]: {
    txId: string;
    playerNote: string;
    actionType: string;
  } | null;
  [GeneralModal.APPROVE_REJECT_AFFILIATE_CONFIRMATION]: {
    txId: string;
    playerNote: string;
    actionType: string;
  } | null;
  [GeneralModal.APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION]: {
    uuid: string;
    playerNote: string;
    actionType: string;
    playerCode: string;
  } | null;
  [GeneralModal.APPROVE_REFERRAL_CLAIM_CONFIRMATION]: ApproveReferralClaimDto | null;
  [GeneralModal.EDIT_OPERATOR_BILLING_RECORD_PAYMENT]: OperatorPayment | null;
  [GeneralModal.EDIT_OPERATOR_BILLING_RECORD_INVOICE]: OperatorInvoice | null;
  [GeneralModal.EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2]: OperatorInvoice | null;
  [GeneralModal.DELETE_OPERATOR_BILLING_RECORD_PAYMENT]: OperatorPayment | null;
  [GeneralModal.DELETE_OPERATOR_BILLING_RECORD_INVOICE]: OperatorInvoice | null;
  [GeneralModal.EDIT_BRAND_BILLING_RECORD_PAYMENT]: OperatorPayment | null;
  [GeneralModal.EDIT_BRAND_BILLING_RECORD_INVOICE]: OperatorInvoice | null;
  [GeneralModal.DELETE_BRAND_BILLING_RECORD_PAYMENT]: OperatorPayment | null;
  [GeneralModal.DELETE_BRAND_BILLING_RECORD_INVOICE]: OperatorInvoice | null;

  [GeneralModal.DELETE_TRANSACTION]: { txId: string } | null;
  [GeneralModal.CONVERT_PLAYER_BALANCE]: {
    fromCurrency: string;
    playerId: string;
    playerWalletList: string[];
  } | null;
  [GeneralModal.ADD_SPORT_FREEBET_CONFIRMATION]: {
    templateId: number;
    betCount: number;
    betAmount: number;
    playerId: string;
  } | null;
  [GeneralModal.FORCE_APPROVE_JB_WITHDRAW]: {
    response: AddJbWithdrawRequestResponse;
    txId: string;
    playerNote: string;
    actionType: string;
  } | null;
  [GeneralModal.INFORMATION_ICON]: {
    slug: string;
  } | null;
  [GeneralModal.HELP_INFO]: { content: ReactNode } | null;
  [GeneralModal.ERROR_RESPONSE]: {
    messages: string[];
    title?: string;
  } | null;
  [GeneralModal.AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION]:
    | (WithdrawAffiliateProfitDto & { totalProfitInUSD: number })
    | null;
  [GeneralModal.APPROVE_TOMANI_WITHDRAW]: {
    opId: string;
    txId: string;
    playerNote: string;
    paymentProvider: string;
  } | null;
  [GeneralModal.DELETE_DEPOSIT_GATEWAY_CONFIRMATION]: {
    opId: string;
    provider: PaymentProviderName;
    uuid: string;
  } | null;
  [GeneralModal.DELETE_WITHDRAW_GATEWAY_CONFIRMATION]: {
    opId: string;
    provider: PaymentProviderName;
    uuid: string;
  } | null;
  [GeneralModal.PLAYER_CASHBACK_CLAIM_CONFIRMATION]: (GetPlayerCashbackReportDto & { claimAmountInUSD: number }) | null;
  [GeneralModal.PLAYER_CASHBACK_OVERRIDE_SETTINGS]: {
    player: Player;
    reportData: IPlayerCashbackReport;
    settings: IPlayerCashbackSettingsReport;
    descriptions: CashbackDescriptions | undefined;
  } | null;
  [GeneralModal.AFFILIATE_PROFIT_CALCULATOR]:
    | (TestCalculateProfitDto & {
        descriptions: AffiliateSettingsHelp | undefined;
      })
    | null;
  [GeneralModal.PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT]: TestCalculateProfitDto | null;
  [GeneralModal.AFFILIATE_LEVEL_SETTINGS]: {
    data: IAffiliateLevelSettingsPPF[] | null | undefined;
    currentLevel: {
      currentLevel: number;
      nextLevel: number;
      currentLevelPercentage: number;
      referredPlayersWagerInUSD: number;
      nextLevelRequiredWagerInUSD: number;
      currentLevelSettings?: IAffiliateLevelSettings | null;
      currentRevenueShareSettings?: IAffiliateRevenueShareSettings | null;
    };
    revenueShareSettings: IAffiliateRevenueShareSettingsPPF[];
    descriptions: AffiliateSettingsHelp | undefined;
  } | null;
  [GeneralModal.PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS]: ResetPlayerSettingsOverridesDto | null;
  [GeneralModal.PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS]: ResetPlayerSettingsOverridesDto | null;
  [GeneralModal.PLAYER_AFFILIATE_OVERRIDE_SETTINGS]:
    | (OverridePlayerAffiliateSettingsDto & {
        descriptions: AffiliateReportsHelpInfo | undefined;
      })
    | null;
}

const initialState: ModalContentSliceState = {
  PLAYER_DELETE_CONFIRMATION: null,
  VERIFY_DEPOSIT_CONFIRMATION: null,
  CHECK_ORDER_STATUS: null,
  APPROVE_REJECT_WITHDRAW_CONFIRMATION: null,
  APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION: null,
  APPROVE_REFERRAL_CLAIM_CONFIRMATION: null,
  EDIT_OPERATOR_BILLING_RECORD_PAYMENT: null,
  EDIT_OPERATOR_BILLING_RECORD_INVOICE: null,
  EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2: null,
  DELETE_OPERATOR_BILLING_RECORD_PAYMENT: null,
  DELETE_OPERATOR_BILLING_RECORD_INVOICE: null,
  DELETE_TRANSACTION: null,
  DELETE_BRAND_BILLING_RECORD_INVOICE: null,
  DELETE_BRAND_BILLING_RECORD_PAYMENT: null,
  EDIT_BRAND_BILLING_RECORD_INVOICE: null,
  EDIT_BRAND_BILLING_RECORD_PAYMENT: null,

  CONVERT_PLAYER_BALANCE: null,
  REJECT_DEPOSIT_ORDER: null,
  ADD_SPORT_FREEBET_CONFIRMATION: null,
  APPROVE_REJECT_BONUS_CONFIRMATION: null,
  FORCE_APPROVE_JB_WITHDRAW: null,
  INFORMATION_ICON: null,
  HELP_INFO: null,
  LAUNCH_GAME_URL: null,
  ERROR_RESPONSE: null,
  AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION: null,
  APPROVE_TOMANI_WITHDRAW: null,
  DELETE_DEPOSIT_GATEWAY_CONFIRMATION: null,
  DELETE_WITHDRAW_GATEWAY_CONFIRMATION: null,
  PLAYER_CASHBACK_CLAIM_CONFIRMATION: null,
  PLAYER_CASHBACK_OVERRIDE_SETTINGS: null,
  AFFILIATE_PROFIT_CALCULATOR: null,
  PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT: null,
  AFFILIATE_LEVEL_SETTINGS: null,
  PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS: null,
  PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS: null,
  PLAYER_AFFILIATE_OVERRIDE_SETTINGS: null,
  APPROVE_REJECT_CASHBACK_CONFIRMATION: null,
  APPROVE_REJECT_AFFILIATE_CONFIRMATION: null,
};

export const modalContentSlice = createSlice({
  name: 'modalContent',
  initialState,
  reducers: {
    setPlayerDeleteConfirmation: (state, action: PayloadAction<{ playerId: string }>) => {
      state[GeneralModal.PLAYER_DELETE_CONFIRMATION] = action.payload;
    },
    setVerifyDepositConfirmation: (state, action: PayloadAction<{ txId: string; isNewVersion?: boolean }>) => {
      state[GeneralModal.VERIFY_DEPOSIT_CONFIRMATION] = action.payload;
    },
    setApproveBankAccountConfirmation: (
      state,
      action: PayloadAction<{
        uuid: string;
        actionType: string;
        playerNote: string;
        playerCode: string;
      }>
    ) => {
      state[GeneralModal.APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION] = action.payload;
    },
    setApproveWithdrawConfirmation: (
      state,
      action: PayloadAction<{
        txId: string;
        actionType: string;
        playerNote: string;
        isNewVersion?: boolean;
        paymentProvider: string;
        status: TransactionStatus;
      }>
    ) => {
      state[GeneralModal.APPROVE_REJECT_WITHDRAW_CONFIRMATION] = action.payload;
    },
    setApproveBonusConfirmation: (
      state,
      action: PayloadAction<{
        txId: string;
        actionType: string;
        playerNote: string;
      }>
    ) => {
      state[GeneralModal.APPROVE_REJECT_BONUS_CONFIRMATION] = action.payload;
    },
    setApproveRejectCashbackConfirmation: (
      state,
      action: PayloadAction<{
        txId: string;
        actionType: string;
        playerNote: string;
      }>
    ) => {
      state[GeneralModal.APPROVE_REJECT_CASHBACK_CONFIRMATION] = action.payload;
    },
    setApproveRejectWithdrawAffiliateConfirmation: (
      state,
      action: PayloadAction<{
        txId: string;
        actionType: string;
        playerNote: string;
      }>
    ) => {
      state[GeneralModal.APPROVE_REJECT_AFFILIATE_CONFIRMATION] = action.payload;
    },
    setApproveReferralClaimConfirmation: (state, action: PayloadAction<ApproveReferralClaimDto>) => {
      state[GeneralModal.APPROVE_REFERRAL_CLAIM_CONFIRMATION] = action.payload;
    },
    setOperatorEditBillingRecordPayment: (state, action: PayloadAction<OperatorPayment>) => {
      state[GeneralModal.EDIT_OPERATOR_BILLING_RECORD_PAYMENT] = action.payload;
    },
    setOperatorEditBillingRecordInvoice: (state, action: PayloadAction<OperatorInvoice>) => {
      state[GeneralModal.EDIT_OPERATOR_BILLING_RECORD_INVOICE] = action.payload;
    },
    setOperatorEditBillingRecordV2Invoice: (state, action: PayloadAction<OperatorInvoice>) => {
      state[GeneralModal.EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2] = action.payload;
    },
    setOperatorDeleteBillingRecordPayment: (state, action: PayloadAction<OperatorPayment>) => {
      state[GeneralModal.DELETE_OPERATOR_BILLING_RECORD_PAYMENT] = action.payload;
    },
    setOperatorDeleteBillingRecordInvoice: (state, action: PayloadAction<OperatorInvoice>) => {
      state[GeneralModal.DELETE_OPERATOR_BILLING_RECORD_INVOICE] = action.payload;
    },
    setBrandEditBillingRecordPayment: (state, action: PayloadAction<OperatorPayment>) => {
      state[GeneralModal.EDIT_BRAND_BILLING_RECORD_PAYMENT] = action.payload;
    },
    setBrandEditBillingRecordInvoice: (state, action: PayloadAction<OperatorInvoice>) => {
      state[GeneralModal.EDIT_BRAND_BILLING_RECORD_INVOICE] = action.payload;
    },
    setBrandDeleteBillingRecordPayment: (state, action: PayloadAction<OperatorPayment>) => {
      state[GeneralModal.DELETE_BRAND_BILLING_RECORD_PAYMENT] = action.payload;
    },
    setBrandDeleteBillingRecordInvoice: (state, action: PayloadAction<OperatorInvoice>) => {
      state[GeneralModal.DELETE_BRAND_BILLING_RECORD_INVOICE] = action.payload;
    },

    setDeleteTransaction: (state, action: PayloadAction<{ txId: string }>) => {
      state[GeneralModal.DELETE_TRANSACTION] = action.payload;
    },
    setConvertPlayerBalance: (
      state,
      action: PayloadAction<{
        fromCurrency: string;
        playerId: string;
        playerWalletList: string[];
      }>
    ) => {
      state[GeneralModal.CONVERT_PLAYER_BALANCE] = action.payload;
    },
    setCheckOrderStatus: (state, action: PayloadAction<unknown>) => {
      state[GeneralModal.CHECK_ORDER_STATUS] = action.payload;
    },
    setLaunchGameURL: (state, action: PayloadAction<{ link: string }>) => {
      state[GeneralModal.LAUNCH_GAME_URL] = action.payload;
    },
    setRejectDepositOrder: (state, action: PayloadAction<{ txId: string }>) => {
      state[GeneralModal.REJECT_DEPOSIT_ORDER] = action.payload;
    },
    setAddSportFreeBetConfirmation: (
      state,
      action: PayloadAction<{
        templateId: number;
        betCount: number;
        betAmount: number;
        playerId: string;
      }>
    ) => {
      state[GeneralModal.ADD_SPORT_FREEBET_CONFIRMATION] = action.payload;
    },
    setForceApproveJbWithdraw: (
      state,
      action: PayloadAction<{
        response: AddJbWithdrawRequestResponse;
        txId: string;
        playerNote: string;
        actionType: string;
      }>
    ) => {
      state[GeneralModal.FORCE_APPROVE_JB_WITHDRAW] = action.payload;
    },
    setInformationIcon: (
      state,
      action: PayloadAction<{
        slug: string;
      }>
    ) => {
      state[GeneralModal.INFORMATION_ICON] = action.payload;
    },
    setHelpInfo: (state, action: PayloadAction<{ content: ReactNode }>) => {
      state[GeneralModal.HELP_INFO] = action.payload;
    },
    setErrorResponse: (
      state,
      action: PayloadAction<{
        messages: string[];
        title?: string;
      }>
    ) => {
      state[GeneralModal.ERROR_RESPONSE] = action.payload;
    },
    setAffiliateWithdrawProfitConfirmation: (
      state,
      action: PayloadAction<WithdrawAffiliateProfitDto & { totalProfitInUSD: number }>
    ) => {
      state[GeneralModal.AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION] = action.payload;
    },
    setApproveTomaniWithdraw: (
      state,
      action: PayloadAction<{
        opId: string;
        txId: string;
        playerNote: string;
        paymentProvider: string;
      }>
    ) => {
      state[GeneralModal.APPROVE_TOMANI_WITHDRAW] = action.payload;
    },
    setDeleteDepositGateway: (
      state,
      action: PayloadAction<{
        opId: string;
        provider: PaymentProviderName;
        uuid: string;
      }>
    ) => {
      state[GeneralModal.DELETE_DEPOSIT_GATEWAY_CONFIRMATION] = action.payload;
    },
    setDeleteWithdrawGateway: (
      state,
      action: PayloadAction<{
        opId: string;
        provider: PaymentProviderName;
        uuid: string;
      }>
    ) => {
      state[GeneralModal.DELETE_WITHDRAW_GATEWAY_CONFIRMATION] = action.payload;
    },
    setPlayerCashbackClaimConfirmation: (
      state,
      action: PayloadAction<GetPlayerCashbackReportDto & { claimAmountInUSD: number }>
    ) => {
      state[GeneralModal.PLAYER_CASHBACK_CLAIM_CONFIRMATION] = action.payload;
    },
    setPlayerCashbackOverrideSettings: (
      state,
      action: PayloadAction<{
        player: Player;
        reportData: IPlayerCashbackReport;
        settings: IPlayerCashbackSettingsReport;
        descriptions: CashbackDescriptions | undefined;
      }>
    ) => {
      state[GeneralModal.PLAYER_CASHBACK_OVERRIDE_SETTINGS] = action.payload;
    },
    setOperatorTestCalculateAffiliateProfitModal: (
      state,
      action: PayloadAction<
        TestCalculateProfitDto & {
          descriptions: AffiliateSettingsHelp | undefined;
        }
      >
    ) => {
      state[GeneralModal.AFFILIATE_PROFIT_CALCULATOR] = action.payload;
    },
    setPlayerTestCalculateAffiliateProfitModal: (state, action: PayloadAction<TestCalculateProfitDto>) => {
      state[GeneralModal.PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT] = action.payload;
    },
    setAffiliateAllLevelsSettings: (
      state,
      action: PayloadAction<{
        data: IAffiliateLevelSettingsPPF[] | null | undefined;
        currentLevel: {
          currentLevel: number;
          nextLevel: number;
          currentLevelPercentage: number;
          referredPlayersWagerInUSD: number;
          nextLevelRequiredWagerInUSD: number;
          currentLevelSettings?: IAffiliateLevelSettings | null;
          currentRevenueShareSettings?: IAffiliateRevenueShareSettings | null;
        };
        revenueShareSettings: IAffiliateRevenueShareSettingsPPF[];
        descriptions: AffiliateSettingsHelp | undefined;
      } | null>
    ) => {
      state[GeneralModal.AFFILIATE_LEVEL_SETTINGS] = action.payload;
    },
    setPlayerCashbackRestoreSettingsConfirmation: (state, action: PayloadAction<ResetPlayerSettingsOverridesDto>) => {
      state[GeneralModal.PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS] = action.payload;
    },
    setPlayerAffiliateRestoreSettingsConfirmation: (state, action: PayloadAction<ResetPlayerSettingsOverridesDto>) => {
      state[GeneralModal.PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS] = action.payload;
    },
    setPlayerAffiliateOverrideSettings: (
      state,
      action: PayloadAction<
        OverridePlayerAffiliateSettingsDto & {
          descriptions: AffiliateReportsHelpInfo | undefined;
        }
      >
    ) => {
      state[GeneralModal.PLAYER_AFFILIATE_OVERRIDE_SETTINGS] = action.payload;
    },
  },
});

// Export the action creators
export const {
  setPlayerDeleteConfirmation,
  setVerifyDepositConfirmation,
  setApproveWithdrawConfirmation,
  setApproveReferralClaimConfirmation,
  setApproveBankAccountConfirmation,
  setOperatorDeleteBillingRecordInvoice,
  setOperatorDeleteBillingRecordPayment,
  setDeleteTransaction,
  setOperatorEditBillingRecordInvoice,
  setOperatorEditBillingRecordV2Invoice,
  setOperatorEditBillingRecordPayment,
  setBrandDeleteBillingRecordInvoice,
  setBrandDeleteBillingRecordPayment,
  setBrandEditBillingRecordInvoice,
  setBrandEditBillingRecordPayment,

  setConvertPlayerBalance,
  setCheckOrderStatus,
  setRejectDepositOrder,
  setAddSportFreeBetConfirmation,
  setApproveBonusConfirmation,
  setForceApproveJbWithdraw,
  setInformationIcon,
  setLaunchGameURL,
  setErrorResponse,
  setAffiliateWithdrawProfitConfirmation,
  setHelpInfo,
  setApproveTomaniWithdraw,
  setDeleteDepositGateway,
  setDeleteWithdrawGateway,
  setPlayerCashbackClaimConfirmation,
  setPlayerCashbackOverrideSettings,
  setOperatorTestCalculateAffiliateProfitModal,
  setPlayerTestCalculateAffiliateProfitModal,
  setAffiliateAllLevelsSettings,
  setPlayerCashbackRestoreSettingsConfirmation,
  setPlayerAffiliateRestoreSettingsConfirmation,
  setPlayerAffiliateOverrideSettings,
  setApproveRejectCashbackConfirmation,
  setApproveRejectWithdrawAffiliateConfirmation,
} = modalContentSlice.actions;

export const getModalContent = (state: RootState): ModalContentSliceState => state.modalContent;
